/**
 * VqeEditStatus — the inline "✓ Edit applied · Undo" status a VQE surface
 * shows in its own chrome after one of its edits lands (see
 * `useEditAppliedStatus` for when it shows and hides). Renders nothing while
 * hidden. The Undo button runs the same undo as Cmd/Ctrl+Z on that surface;
 * the chord hint uses the platform's own label (⌘Z on macOS, Ctrl+Z elsewhere).
 */
import { Check, Undo2 } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { formatChord } from '../../../keybindings/model/chord';
import { platform } from '../../../keybindings/model/platform';
import { vqeCopy } from './vqeCopy';

export interface VqeEditStatusProps {
  readonly visible: boolean;
  readonly onUndo: () => void;
  readonly className?: string;
}

export function VqeEditStatus({ visible, onUndo, className }: VqeEditStatusProps) {
  if (!visible) return null;
  const chord = formatChord('Mod+Z', platform());
  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="vqe-edit-status"
      className={`inline-flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground ${className ?? ''}`}
    >
      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
      <span className="truncate">{vqeCopy.toast.successTitle}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 gap-1 px-1.5 text-xs"
        onClick={onUndo}
        aria-keyshortcuts={chord}
        data-testid="vqe-edit-status-undo"
      >
        <Undo2 className="h-3.5 w-3.5" aria-hidden />
        {vqeCopy.toast.undo}
      </Button>
      <kbd
        aria-label={vqeCopy.toast.undoHint(chord)}
        className="hidden rounded border border-border px-1 font-mono text-[10px] sm:inline"
      >
        {chord}
      </kbd>
    </span>
  );
}
